import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import PopOver from '../components/deprecated/PopOver';
import IconButton from '../components/IconButton';
import SpringAnimation from '../components/SpringAnimation';
import Tether from '../components/Tether';
import Tooltip from '../components/Tooltip';
import { useQuill } from '../editor/QuillContext';
import tocIcon from '../icons/non-prefilled/16x16/tableOfContentsNew.svg';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import { usePostLayoutSize } from '../utils/responsive/PostLayoutSizeProvider';
import useEventListener from '../utils/useEventListener';
import useIsPublicView from '../utils/useIsPublicView';
import type { TOCState } from './actionButtons/ConnectedTableOfContents';
import ConnectedTableOfContents from './actionButtons/ConnectedTableOfContents';
import TableOfContents from './actionButtons/TableOfContents';
import { usePostLayoutState, useToggleTOC } from './PostLayoutProvider';
import * as styles from './PostStickyControl.module.scss';
import SVGIcon from '../components/SVGIcon';
import { isDraggingAtom } from '../editor/reorderGutterAtoms';

interface TocButtonProps {
  onClick: () => void;
  ariaLabel: string;
  hasHeaders: boolean;
}

const TocButton = React.forwardRef<HTMLButtonElement, TocButtonProps>(
  ({ onClick, ariaLabel, hasHeaders }, ref) => {
    const isDisabled =
      useAppSelector((state) => state.editor.editable) === false && !hasHeaders;

    const button = (
      <IconButton
        ref={ref}
        onClick={onClick}
        aria-label={ariaLabel}
        className={styles.tocButton}
        disabled={isDisabled}
      >
        {tocIcon}
      </IconButton>
    );

    if (hasHeaders || isDisabled) {
      return <>{button}</>;
    }

    return (
      <Tooltip
        content={
          'Add headings to your post\nto automatically add\na table of contents'
        }
        placement="right"
        trigger="clickOrHover"
      >
        {button}
      </Tooltip>
    );
  },
);

interface ScrollableContentContainerProps {
  children: React.ReactNode;
}

const ScrollableContentContainer = ({
  children,
}: ScrollableContentContainerProps) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  useEventListener(container, 'scroll', () => {
    container?.classList.toggle(styles.scrolled, container.scrollTop > 0);
  });

  return (
    <div ref={setContainer} className={styles.content}>
      {children}
    </div>
  );
};

interface TOCPopOverProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  open: boolean;
  tocState: TOCState;
}

const TOCPopOver = React.forwardRef<HTMLDivElement, TOCPopOverProps>(
  ({ open, style, tocState, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        style={{
          ...style,
          pointerEvents: open ? 'auto' : 'none',
        }}
        className={styles.popOver}
        {...rest}
      >
        <AnimatePresence>
          {open && (
            <SpringAnimation
              direction="down"
              className={styles.tocPopOverContainer}
            >
              <PopOver.Container width="auto">
                <div className={styles.tocPopOverContent}>
                  <h4 className={styles.title}>Table of Contents</h4>
                  <TableOfContents {...tocState} />
                </div>
              </PopOver.Container>
            </SpringAnimation>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

interface PostStickyControlProps {
  postId: string;
  hasBanner: boolean;
}

const PostStickyControl = ({ postId, hasBanner }: PostStickyControlProps) => {
  const [titleLineAnchorElement, setTitleLineAnchorElement] =
    useState<HTMLElement | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const isPublicView = useIsPublicView();
  const [isTOCPopoverOpen, setIsTOCPopoverOpen] = useState(false);
  const { quill, readOnly } = useQuill();
  const toggleTOC = useToggleTOC();
  const { isTOCExpanded, canTOCExpand } = usePostLayoutState();
  const postLayoutSize = usePostLayoutSize();
  const isReorderingWithDragAndDrop = useAtomValue(isDraggingAtom);

  useEffect(() => {
    if (!quill || postLayoutSize !== 'mobile') return undefined;
    const titleLine = quill.titleLine?.domNode;

    if (!(titleLine instanceof Element)) return undefined;

    if (!readOnly) {
      setIsTOCPopoverOpen(false);
      return undefined;
    }

    const onTitleClick = () => {
      setIsTOCPopoverOpen((isOpen) => !isOpen);
    };

    setTitleLineAnchorElement(titleLine);
    titleLine.addEventListener('click', onTitleClick);

    return () => {
      titleLine.removeEventListener('click', onTitleClick);
    };
  }, [quill, postLayoutSize, toggleTOC, readOnly]);

  return (
    <ConnectedTableOfContents postId={postId}>
      {(tocProps) => {
        const hasHeaders = tocProps.headers.length > 0;
        const tocButtonAriaLabel = isTOCExpanded
          ? 'Hide table of contents'
          : 'Show table of contents';

        if (postLayoutSize === 'mobile') {
          if (!hasHeaders) return null;

          return (
            <ClickOutsideHandler
              onClickOutside={(event) => {
                const titleLine = quill?.scroll.children.head?.domNode;
                if (!titleLine?.contains(event.target as Node)) {
                  setIsTOCPopoverOpen(false);
                }
              }}
            >
              <Tether
                placement="bottom-start"
                anchorElement={titleLineAnchorElement}
                positionedView={
                  <TOCPopOver open={isTOCPopoverOpen} tocState={tocProps} />
                }
              />
            </ClickOutsideHandler>
          );
        }

        return (
          <div
            className={classNames(styles.container, {
              [styles.isShowingTOC]: isTOCExpanded,
              [styles.hasBanner]: hasBanner,
              [styles.isPublicView]: isPublicView,
              [styles.tablet]: postLayoutSize === 'tablet',
              [styles.monitor]: postLayoutSize === 'monitor',
              [styles.isReorderingWithDragAndDrop]: isReorderingWithDragAndDrop,
            })}
          >
            <div
              onMouseEnter={() => setIsTOCPopoverOpen(true)}
              onMouseLeave={() => setIsTOCPopoverOpen(false)}
              className={styles.actionButtons}
            >
              <TocButton
                ref={setAnchorElement}
                onClick={() => {
                  if (hasHeaders && canTOCExpand) {
                    toggleTOC();
                  }
                }}
                ariaLabel={tocButtonAriaLabel}
                hasHeaders={hasHeaders}
              />
              {hasHeaders && !canTOCExpand && (
                <Tether
                  placement="bottom-start"
                  anchorElement={anchorElement}
                  positionedView={
                    <TOCPopOver open={isTOCPopoverOpen} tocState={tocProps} />
                  }
                />
              )}
            </div>
            {canTOCExpand && (
              <motion.nav
                key="toc"
                initial={false}
                animate={{
                  opacity: isTOCExpanded ? 1 : 0,
                  x: isTOCExpanded ? '0%' : '-50%',
                }}
                transition={{ type: 'tween', duration: 0.15 }}
                className={styles.tocContainer}
              >
                <h4 className={styles.title}>
                  Table of Contents
                  <Button
                    className={styles.closeAreaButton}
                    aria-label={tocButtonAriaLabel}
                    onClick={() => toggleTOC()}
                  />
                </h4>
                <ScrollableContentContainer>
                  <TableOfContents {...tocProps} />
                </ScrollableContentContainer>
              </motion.nav>
            )}

            {isTOCExpanded && (
              <>
                <Button
                  className={styles.closeAreaButton}
                  aria-label={tocButtonAriaLabel}
                  onClick={() => toggleTOC()}
                />
                <div
                  className={classNames(styles.tocHoverArea, styles.alignLeft)}
                />
              </>
            )}
          </div>
        );
      }}
    </ConnectedTableOfContents>
  );
};

interface TOCInMagneticWallProps {
  postId: string;
  hasBanner: boolean;
}

export const TOCInMagneticWall = ({
  postId,
  hasBanner,
}: TOCInMagneticWallProps) => {
  const { isMagneticWallExpanded } = usePostLayoutState();
  const postLayoutSize = usePostLayoutSize();

  if (!isMagneticWallExpanded) {
    return null;
  }

  return (
    <ConnectedTableOfContents postId={postId}>
      {(tocProps) => (
        <div
          className={classNames(
            styles.container,
            styles.isPublicView,
            styles.isInMagneticWall,
            styles.isShowingTOC,
            {
              [styles.hasBanner]: hasBanner,
              [styles.monitor]: postLayoutSize === 'monitor',
            },
          )}
        >
          <div className={classNames(styles.tocHoverArea, styles.alignRight)} />
          <div className={styles.tocIcon}>
            <SVGIcon inline>{tocIcon}</SVGIcon>
          </div>
          <div className={styles.tocContainer}>
            <h4 className={styles.title}>Table of Contents</h4>
            <ScrollableContentContainer>
              <TableOfContents {...tocProps} />
            </ScrollableContentContainer>
          </div>
        </div>
      )}
    </ConnectedTableOfContents>
  );
};

export default PostStickyControl;
